import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Drawer, 
  AppBar, 
  Toolbar, 
  List, 
  Typography, 
  IconButton, 
  ListItem, 
  ListItemButton, 
  ListItemIcon, 
  ListItemText,
  Chip,
  Collapse,
  Snackbar,
  Alert,
  Button
} from '@mui/material';
import { 
  LayoutDashboard, 
  FileText, 
  LogOut, 
  Users,
  Settings,
  Layers,
  FolderOpen,
  MessageCircle,
  Briefcase,
  Building2,
  Bot,
  ChevronDown,
  ChevronRight,
  FolderKanban,
  Clock,
  BookOpen
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useSocket } from '../context/SocketContext';
import { Link, useLocation } from 'react-router-dom';
import NotificationBell from '../components/NotificationBell';

const drawerWidth = 240;

// Statuts qui définissent un projet (vs devis)
const PROJECT_STATUSES = ['ACCEPTED', 'DEVELOPMENT', 'COMPLETED'];

function MainLayout({ children }) {
  const { user, logout, sessionWarning, timeRemaining } = useAuth();
  const { socket, joinSubmission, leaveSubmission } = useSocket();
  const location = useLocation();
  const [documentCount, setDocumentCount] = useState(0);
  const [unreadCount, setUnreadCount] = useState(0);
  const [projectCount, setProjectCount] = useState(0);
  const [devisCount, setDevisCount] = useState(0);
  const [currentSubmissionStatus, setCurrentSubmissionStatus] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [settingsExpanded, setSettingsExpanded] = useState(false);

  // Detect if we're in a specific submission context
  const submissionMatch = location.pathname.match(/\/(admin|portal)\/submissions\/([a-f0-9-]+)/);
  const currentSubmissionId = submissionMatch ? submissionMatch[2] : null;
  const isAdminPath = submissionMatch ? submissionMatch[1] === 'admin' : location.pathname.startsWith('/admin');

  // Determine if current submission is a project or devis
  const isCurrentProject = currentSubmissionStatus && PROJECT_STATUSES.includes(currentSubmissionStatus);

  // Socket listeners for real-time updates
  useEffect(() => {
    if (!socket) return;

    const handleStatusChanged = () => setRefreshTrigger(prev => prev + 1);
    const handleProjectCountChanged = () => setRefreshTrigger(prev => prev + 1);
    const handleNewMessage = () => setRefreshTrigger(prev => prev + 1);
    const handleDocumentChanged = () => setRefreshTrigger(prev => prev + 1);

    socket.on('statusChanged', handleStatusChanged);
    socket.on('projectCountChanged', handleProjectCountChanged);
    socket.on('newMessage', handleNewMessage);
    socket.on('documentChanged', handleDocumentChanged);

    return () => {
      socket.off('statusChanged', handleStatusChanged);
      socket.off('projectCountChanged', handleProjectCountChanged);
      socket.off('newMessage', handleNewMessage);
      socket.off('documentChanged', handleDocumentChanged);
    };
  }, [socket]);

  // Join/leave submission room when in submission context
  useEffect(() => {
    if (currentSubmissionId) {
      joinSubmission(currentSubmissionId);
    }
    return () => {
      if (currentSubmissionId) {
        leaveSubmission(currentSubmissionId);
      }
    };
  }, [currentSubmissionId, joinSubmission, leaveSubmission]);

  // Fetch project count and devis count
  useEffect(() => {
    const fetchCounts = async () => {
      try {
        const token = localStorage.getItem('portalToken');
        const [projRes, devisRes] = await Promise.all([
          fetch('/api/submissions/projects/count', {
            headers: { 'Authorization': `Bearer ${token}` }
          }),
          fetch('/api/submissions/devis/count', {
            headers: { 'Authorization': `Bearer ${token}` }
          })
        ]);
        if (projRes.ok) {
          const data = await projRes.json();
          setProjectCount(data.count);
        }
        if (devisRes.ok) {
          const data = await devisRes.json();
          setDevisCount(data.count);
        }
      } catch (err) {
        console.error('Fetch counts error');
      }
    };
    fetchCounts();
  }, [location.pathname, refreshTrigger]);

  // Fetch submission data when in submission context
  useEffect(() => {
    if (!currentSubmissionId) {
      setCurrentSubmissionStatus(null);
      return;
    }

    const fetchSubmissionData = async () => {
      try {
        const token = localStorage.getItem('portalToken');
        
        // Fetch submission to get status
        const subResponse = await fetch(`/api/submissions/${currentSubmissionId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (subResponse.ok) {
          const subData = await subResponse.json();
          setCurrentSubmissionStatus(subData.status);
        }
        
        // Fetch document count
        const docResponse = await fetch(`/api/submissions/${currentSubmissionId}/documents/count`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (docResponse.ok) {
          const data = await docResponse.json();
          setDocumentCount(data.count);
        }

        // Fetch unread messages count
        const msgResponse = await fetch(`/api/submissions/${currentSubmissionId}/messages/unread`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (msgResponse.ok) {
          const data = await msgResponse.json();
          setUnreadCount(data.count);
        }
      } catch (err) {
        console.error('Fetch submission data error');
      }
    };
    
    fetchSubmissionData();
  }, [currentSubmissionId, location.pathname, refreshTrigger]);

  // Helper functions for role checks
  const isSuperAdmin = user?.role === 'SUPERADMIN';
  const isAdmin = user?.role === 'ADMIN' || user?.role === 'SUPERADMIN';
  const isDev = user?.role === 'DEV' || user?.role === 'TECHNICIEN';
  const isClientAdmin = user?.role === 'CLIENTADMIN';
  const isClient = user?.role === 'CLIENT';
  const isInternalRole = isAdmin || isDev;

  // Auto-expand settings menu if on a settings sub-page
  const isOnSettingsSubPage = location.pathname.startsWith('/admin/project-types') ||
                              location.pathname.startsWith('/admin/llm') ||
                              location.pathname.startsWith('/admin/settings/') ||
                              location.pathname === '/settings';
  
  useEffect(() => {
    if (isOnSettingsSubPage) {
      setSettingsExpanded(true);
    }
  }, [isOnSettingsSubPage]);

  // Build menu items dynamically based on role
  const getMenuItems = () => {
    // TECHNICIEN: Dashboard, Devis, Projets + Mon compte seulement
    if (user?.role === 'TECHNICIEN') {
      return [
        { text: 'Dashboard', icon: <LayoutDashboard size={20} />, path: '/admin' },
        { text: 'Devis', icon: <FileText size={20} />, path: '/admin/submissions', type: 'devis', count: devisCount },
        ...(projectCount > 0 ? [{ text: 'Projets', icon: <Briefcase size={20} />, path: '/admin/projects', type: 'projects', count: projectCount }] : []),
        { text: 'Mon compte', icon: <Settings size={20} />, path: '/settings' },
      ];
    }

    // DEV: Dashboard, Devis, Projets + Paramètres complets (comme SUPERADMIN)
    if (user?.role === 'DEV') {
      return [
        { text: 'Dashboard', icon: <LayoutDashboard size={20} />, path: '/admin' },
        { text: 'Devis', icon: <FileText size={20} />, path: '/admin/submissions', type: 'devis', count: devisCount },
        ...(projectCount > 0 ? [{ text: 'Projets', icon: <Briefcase size={20} />, path: '/admin/projects', type: 'projects', count: projectCount }] : []),
        {
          text: 'Paramètres',
          icon: <Settings size={20} />,
          type: 'settings',
          subItems: [
            { text: 'Général', icon: <Settings size={18} />, path: '/admin/settings/general' },
            { text: 'Annexe PDF', icon: <BookOpen size={18} />, path: '/admin/settings/annexe' },
            { text: 'Types de projets', icon: <FolderKanban size={18} />, path: '/admin/project-types' },
            { text: 'Configuration AI', icon: <Bot size={18} />, path: '/admin/llm' },
            { text: 'Mon compte', icon: <Settings size={18} />, path: '/settings' },
          ]
        },
      ];
    }

    // ADMIN / SUPERADMIN: accès complet
    if (isAdmin) {
      return [
        { text: 'Dashboard', icon: <LayoutDashboard size={20} />, path: '/admin' },
        { text: 'Devis', icon: <FileText size={20} />, path: '/admin/submissions', type: 'devis', count: devisCount },
        ...(projectCount > 0 ? [{ text: 'Projets', icon: <Briefcase size={20} />, path: '/admin/projects', type: 'projects', count: projectCount }] : []),
        { text: 'Templates', icon: <Layers size={20} />, path: '/admin/templates' },
        { text: 'Entreprises', icon: <Building2 size={20} />, path: '/admin/companies' },
        { text: 'Utilisateurs', icon: <Users size={20} />, path: '/admin/clients' },
        {
          text: 'Paramètres',
          icon: <Settings size={20} />,
          type: 'settings',
          subItems: [
            { text: 'Général', icon: <Settings size={18} />, path: '/admin/settings/general' },
            { text: 'Annexe PDF', icon: <BookOpen size={18} />, path: '/admin/settings/annexe' },
            { text: 'Types de projets', icon: <FolderKanban size={18} />, path: '/admin/project-types' },
            ...(isSuperAdmin ? [{ text: 'Configuration AI', icon: <Bot size={18} />, path: '/admin/llm' }] : []),
            { text: 'Mon compte', icon: <Settings size={18} />, path: '/settings' },
          ]
        },
      ];
    }

    // CLIENTADMIN: Dashboard, Devis, Projets + Gestion des utilisateurs de son entreprise
    if (isClientAdmin) {
      return [
        { text: 'Dashboard', icon: <LayoutDashboard size={20} />, path: '/portal' },
        { text: 'Mes Devis', icon: <FileText size={20} />, path: '/portal/submissions', type: 'devis', count: devisCount },
        ...(projectCount > 0 ? [{ text: 'Mes Projets', icon: <Briefcase size={20} />, path: '/portal/projects', type: 'projects', count: projectCount }] : []),
        { text: 'Utilisateurs', icon: <Users size={20} />, path: '/portal/users' },
        { text: 'Paramètres', icon: <Settings size={20} />, path: '/settings' },
      ];
    }

    // CLIENT: accès standard
    return [
      { text: 'Dashboard', icon: <LayoutDashboard size={20} />, path: '/portal' },
      { text: 'Mes Devis', icon: <FileText size={20} />, path: '/portal/submissions', type: 'devis', count: devisCount },
      ...(projectCount > 0 ? [{ text: 'Mes Projets', icon: <Briefcase size={20} />, path: '/portal/projects', type: 'projects', count: projectCount }] : []),
      { text: 'Paramètres', icon: <Settings size={20} />, path: '/settings' },
    ];
  };

  const baseMenuItems = getMenuItems();

  // Logic to determine if a menu item is selected precisely
  const isSelected = (path, type) => {
    if (path === '/admin' || path === '/portal') {
      return location.pathname === path;
    }
    // Special handling for submissions path when viewing a submission
    if (type === 'devis' && currentSubmissionId && !isCurrentProject) {
      return location.pathname.includes('/submissions');
    }
    if (type === 'projects' && currentSubmissionId && isCurrentProject) {
      return location.pathname.includes('/submissions');
    }
    return location.pathname.startsWith(path);
  };

  // Check if sub-menus should be highlighted
  const isDocumentsSelected = currentSubmissionId && location.pathname.includes('/documents');
  const isEchangesSelected = currentSubmissionId && location.pathname.includes('/echanges');

  // Determine which parent should show sub-menus
  const shouldShowSubMenusUnderDevis = currentSubmissionId && !isCurrentProject;
  const shouldShowSubMenusUnderProjects = currentSubmissionId && isCurrentProject;

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar position="fixed" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1, bgcolor: 'background.paper', backgroundImage: 'none', borderBottom: '1px solid rgba(255, 255, 255, 0.06)' }}>
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          <Box 
            component="img" 
            src="/logo.png" 
            alt="GlobalTI Logo" 
            sx={{ height: 45, width: 'auto' }} 
          />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <NotificationBell />
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {user?.email}
              </Typography>
              {user?.companyName && (
                <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block' }}>
                  {user.companyName}
                </Typography>
              )}
            </Box>
            <IconButton onClick={logout} color="primary">
              <LogOut size={20} />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>
      <Drawer
        variant="permanent"
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          [`& .MuiDrawer-paper`]: { 
            width: drawerWidth, 
            boxSizing: 'border-box', 
            bgcolor: 'background.paper', 
            backgroundImage: 'none', 
            borderRight: '1px solid rgba(255, 255, 255, 0.06)' 
          },
        }}
      >
        <Toolbar />
        <Box sx={{ overflow: 'auto', mt: 2 }}>
          <List sx={{ px: 1 }}>
            {baseMenuItems.map((item, index) => {
              const itemSelected = isSelected(item.path, item.type) && !isDocumentsSelected && !isEchangesSelected;
              const showSubMenus = (item.type === 'devis' && shouldShowSubMenusUnderDevis) || 
                                   (item.type === 'projects' && shouldShowSubMenusUnderProjects);
              const isSettingsMenu = item.type === 'settings';
              const settingsHasActiveChild = isSettingsMenu && item.subItems?.some(sub => location.pathname === sub.path || location.pathname.startsWith(sub.path + '/'));
              
              // Render settings menu with expandable sub-items
              if (isSettingsMenu) {
                return (
                  <React.Fragment key={item.text}>
                    <ListItem disablePadding sx={{ mb: 0.5 }}>
                      <ListItemButton 
                        onClick={() => setSettingsExpanded(!settingsExpanded)}
                        selected={settingsHasActiveChild && !settingsExpanded}
                        sx={{ 
                          borderRadius: '12px', 
                          py: 1.2,
                          '&.Mui-selected': { 
                            bgcolor: 'rgba(0, 212, 170, 0.12)', 
                            color: 'primary.main',
                            '&:hover': { bgcolor: 'rgba(0, 212, 170, 0.18)' }
                          },
                          '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.04)' } 
                        }}
                      >
                        <ListItemIcon sx={{ 
                          color: settingsHasActiveChild ? 'primary.main' : 'inherit', 
                          minWidth: 40 
                        }}>
                          {item.icon}
                        </ListItemIcon>
                        <ListItemText 
                          primary={item.text} 
                          primaryTypographyProps={{ 
                            fontSize: '0.875rem', 
                            fontWeight: settingsHasActiveChild ? 700 : 500 
                          }} 
                        />
                        {settingsExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                      </ListItemButton>
                    </ListItem>
                    
                    <Collapse in={settingsExpanded} timeout="auto" unmountOnExit>
                      <List component="div" disablePadding>
                        {item.subItems?.map((subItem) => {
                          const subSelected = location.pathname === subItem.path || location.pathname.startsWith(subItem.path + '/');
                          return (
                            <ListItem key={subItem.text} disablePadding sx={{ mb: 0.5 }}>
                              <ListItemButton 
                                component={Link}
                                to={subItem.path}
                                selected={subSelected}
                                sx={{ 
                                  borderRadius: '12px', 
                                  py: 1,
                                  pl: 4,
                                  ml: 2,
                                  borderLeft: '2px solid',
                                  borderColor: subSelected ? 'primary.main' : 'rgba(255, 255, 255, 0.1)',
                                  '&.Mui-selected': { 
                                    bgcolor: 'rgba(0, 212, 170, 0.12)', 
                                    color: 'primary.main',
                                    '&:hover': { bgcolor: 'rgba(0, 212, 170, 0.18)' }
                                  },
                                  '&:hover': { 
                                    bgcolor: 'rgba(255, 255, 255, 0.04)',
                                    borderColor: 'primary.main'
                                  } 
                                }}
                              >
                                <ListItemIcon sx={{ 
                                  color: subSelected ? 'primary.main' : 'inherit', 
                                  minWidth: 32 
                                }}>
                                  {subItem.icon}
                                </ListItemIcon>
                                <ListItemText 
                                  primary={subItem.text} 
                                  primaryTypographyProps={{ 
                                    fontSize: '0.8rem', 
                                    fontWeight: subSelected ? 700 : 500 
                                  }} 
                                />
                              </ListItemButton>
                            </ListItem>
                          );
                        })}
                      </List>
                    </Collapse>
                  </React.Fragment>
                );
              }
              
              return (
              <React.Fragment key={item.text}>
                <ListItem disablePadding sx={{ mb: 0.5 }}>
                  <ListItemButton 
                    component={Link}
                    to={item.path}
                    selected={itemSelected}
                    sx={{ 
                      borderRadius: '12px', 
                      py: 1.2,
                      '&.Mui-selected': { 
                        bgcolor: 'rgba(0, 212, 170, 0.12)', 
                        color: 'primary.main',
                        '&:hover': { bgcolor: 'rgba(0, 212, 170, 0.18)' }
                      },
                      '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.04)' } 
                    }}
                  >
                    <ListItemIcon sx={{ 
                      color: itemSelected ? 'primary.main' : 'inherit', 
                      minWidth: 40 
                    }}>
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.text}
                      primaryTypographyProps={{
                        fontSize: '0.875rem',
                        fontWeight: itemSelected ? 700 : 500
                      }}
                    />
                    {item.count > 0 && (
                      <Chip
                        label={item.count}
                        size="small"
                        sx={{
                          height: 20,
                          minWidth: 20,
                          fontSize: '0.65rem',
                          bgcolor: itemSelected ? 'primary.main' : 'rgba(255, 255, 255, 0.1)',
                          color: itemSelected ? '#0a0a0f' : 'text.secondary'
                        }}
                      />
                    )}
                  </ListItemButton>
                </ListItem>

                {/* Contextual Sub-menus - appear after Devis/Projets when in submission context */}
                {showSubMenus && (
                  <>
                    {/* Documents sub-menu */}
                    <ListItem disablePadding sx={{ mb: 0.5 }}>
                      <ListItemButton 
                        component={Link}
                        to={`/${isAdminPath ? 'admin' : 'portal'}/submissions/${currentSubmissionId}/documents`}
                        selected={isDocumentsSelected}
                        sx={{ 
                          borderRadius: '12px', 
                          py: 1,
                          pl: 4,
                          ml: 2,
                          borderLeft: '2px solid',
                          borderColor: isDocumentsSelected ? 'primary.main' : 'rgba(255, 255, 255, 0.1)',
                          '&.Mui-selected': { 
                            bgcolor: 'rgba(0, 212, 170, 0.12)', 
                            color: 'primary.main',
                            '&:hover': { bgcolor: 'rgba(0, 212, 170, 0.18)' }
                          },
                          '&:hover': { 
                            bgcolor: 'rgba(255, 255, 255, 0.04)',
                            borderColor: 'primary.main'
                          } 
                        }}
                      >
                        <ListItemIcon sx={{ 
                          color: isDocumentsSelected ? 'primary.main' : 'inherit', 
                          minWidth: 32 
                        }}>
                          <FolderOpen size={18} />
                        </ListItemIcon>
                        <ListItemText 
                          primary="Documents" 
                          primaryTypographyProps={{ 
                            fontSize: '0.8rem', 
                            fontWeight: isDocumentsSelected ? 700 : 500 
                          }} 
                        />
                        {documentCount > 0 && (
                          <Chip 
                            label={documentCount} 
                            size="small" 
                            sx={{ 
                              height: 20, 
                              minWidth: 20,
                              fontSize: '0.65rem',
                              bgcolor: isDocumentsSelected ? 'primary.main' : 'rgba(255, 255, 255, 0.1)',
                              color: isDocumentsSelected ? '#0a0a0f' : 'text.secondary'
                            }} 
                          />
                        )}
                      </ListItemButton>
                    </ListItem>

                    {/* Échanges sub-menu */}
                    <ListItem disablePadding sx={{ mb: 0.5 }}>
                      <ListItemButton 
                        component={Link}
                        to={`/${isAdminPath ? 'admin' : 'portal'}/submissions/${currentSubmissionId}/echanges`}
                        selected={isEchangesSelected}
                        sx={{ 
                          borderRadius: '12px', 
                          py: 1,
                          pl: 4,
                          ml: 2,
                          borderLeft: '2px solid',
                          borderColor: isEchangesSelected ? 'primary.main' : 'rgba(255, 255, 255, 0.1)',
                          '&.Mui-selected': { 
                            bgcolor: 'rgba(0, 212, 170, 0.12)', 
                            color: 'primary.main',
                            '&:hover': { bgcolor: 'rgba(0, 212, 170, 0.18)' }
                          },
                          '&:hover': { 
                            bgcolor: 'rgba(255, 255, 255, 0.04)',
                            borderColor: 'primary.main'
                          } 
                        }}
                      >
                        <ListItemIcon sx={{ 
                          color: isEchangesSelected ? 'primary.main' : 'inherit', 
                          minWidth: 32 
                        }}>
                          <MessageCircle size={18} />
                        </ListItemIcon>
                        <ListItemText 
                          primary="Échanges" 
                          primaryTypographyProps={{ 
                            fontSize: '0.8rem', 
                            fontWeight: isEchangesSelected ? 700 : 500 
                          }} 
                        />
                        {unreadCount > 0 && (
                          <Chip 
                            label={unreadCount} 
                            size="small" 
                            sx={{ 
                              height: 20, 
                              minWidth: 20,
                              fontSize: '0.65rem',
                              bgcolor: isEchangesSelected ? 'primary.main' : 'rgba(239, 68, 68, 0.8)',
                              color: isEchangesSelected ? '#0a0a0f' : '#fff'
                            }} 
                          />
                        )}
                      </ListItemButton>
                    </ListItem>
                  </>
                )}
              </React.Fragment>
            );
            })}
          </List>
        </Box>
      </Drawer>
      <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
        <Toolbar />
        {children}
      </Box>

      {/* Avertissement d'expiration de session */}
      <Snackbar
        open={sessionWarning}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ top: '80px !important' }}
      >
        <Alert 
          severity="warning" 
          variant="filled"
          icon={<Clock size={20} />}
          sx={{ 
            minWidth: 400,
            alignItems: 'center',
            '& .MuiAlert-message': { display: 'flex', alignItems: 'center', gap: 2 }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body2">
              Votre session expire dans {timeRemaining ? (timeRemaining > 86400 ? Math.ceil(timeRemaining / 86400) + ' jour(s)' : Math.ceil(timeRemaining / 3600) + ' heure(s)') : '?'}
            </Typography>
            <Button 
              size="small" 
              variant="outlined" 
              color="inherit"
              onClick={() => {
                logout();
                window.location.href = '/login';
              }}
              sx={{ ml: 2, borderColor: 'rgba(255,255,255,0.5)', color: 'inherit' }}
            >
              Reconnecter
            </Button>
          </Box>
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default MainLayout;
